import { Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

interface HeroProps {
  onQuickMatch?: () => void;
}

const Hero = ({ onQuickMatch }: HeroProps) => {
  const scrollToTools = () => {
    document.getElementById("tools")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="pt-4 sm:pt-6 pb-5 px-3 sm:px-4 relative" aria-label="Stackly hero">
      <div className="max-w-6xl mx-auto">
        <div
          className="relative overflow-hidden rounded-[28px] sm:rounded-[36px] border border-black/10 dark:border-white/10"
          style={{
            background:
              "radial-gradient(120% 90% at 15% 15%, #f5c06b 0%, transparent 45%)," +
              "radial-gradient(110% 80% at 85% 20%, #b25dff 0%, transparent 50%)," +
              "radial-gradient(120% 100% at 90% 90%, #ff6f9c 0%, transparent 45%)," +
              "radial-gradient(140% 100% at 10% 90%, #2ee6a6 0%, transparent 45%)," +
              "radial-gradient(90% 70% at 50% 50%, #2b6cff 0%, transparent 60%)," +
              "linear-gradient(180deg, #0b0b12 0%, #050508 100%)",
          }}
        >
          {/* grain */}
          <div
            className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />

          <div className="relative z-10 px-5 sm:px-10 md:px-16 pt-10 sm:pt-14 md:pt-16 pb-10 sm:pb-12 md:pb-14 text-center">
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/25 bg-black/50 backdrop-blur-md text-[10px] sm:text-xs font-semibold text-white/95 mb-6 sm:mb-8 whitespace-nowrap"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#2ee6a6] animate-pulse flex-shrink-0" />
              <span className="sm:hidden">For founders, builders and indie hackers</span>
              <span className="hidden sm:inline">For founders, builders & indie hackers</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-white font-bold tracking-[-0.03em] leading-[0.95] text-[2.4rem] xs:text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl mx-auto"
            >
              Find the perfect
              <br className="hidden sm:block" />{" "}
              <span className="italic font-medium text-white/95">AI tool</span>{" "}
              in 60 seconds.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="mt-6 sm:mt-8 text-white/75 text-[15px] sm:text-lg max-w-xl mx-auto leading-relaxed"
            >
               Compare 100+ AI tools. Find the right fit for your next project, then go straight to the tool.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.5 }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <Button
                variant="cta"
                onClick={scrollToTools}
                className="group w-full sm:w-auto px-8 h-12 rounded-full text-[15px]"
              >
                Browse tools
                <ArrowRight className="w-4 h-4 motion-safe:group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                variant="outline"
                onClick={onQuickMatch}
                className="group w-full sm:w-auto px-7 h-12 rounded-full text-[15px]"
              >
                <Sparkles className="w-4 h-4" />
                Quick Match
              </Button>
            </motion.div>

            {/* stats strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-6 sm:mt-8 flex items-stretch justify-center gap-0 w-full sm:max-w-md sm:mx-auto rounded-2xl border border-white/15 bg-black/50 backdrop-blur-md overflow-hidden divide-x divide-white/10"
            >
              {[
                { k: "100+", v: "Tools" },
                { k: "60s", v: "To match" },
                { k: "$0", v: "Forever" },
              ].map((s) => (
                <div key={s.v} className="flex-1 px-2 sm:px-3 py-3 text-center">
                  <div className="text-white text-lg sm:text-2xl font-bold tracking-tight leading-none">{s.k}</div>
                  <div className="text-white/80 text-[10.5px] sm:text-xs mt-1 font-medium">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
